import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import QRCode from "qrcode";
import Layout from "../../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Palette de couleurs SecureControl
const COLORS = {
  primary: '#0B3C5D',     // Bleu Sécurité
  secondary: '#1E88C8',   // Bleu Tech
  accent: '#4FC3F7',      // Bleu Clair
  darkText: '#2E2E2E',    // Gris Foncé
  lightBg: '#F5F7FA',     // Gris Clair
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3',
  successLight: '#e8f5e9',
  dangerLight: '#ffebee',
  warningLight: '#fff3e0',
  infoLight: '#e3f2fd'
};

const TagDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [tag, setTag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serverError, setServerError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [qrImageUrl, setQrImageUrl] = useState(null);

  useEffect(() => {
    fetchTagDetails();
  }, [id]);

  // Génère l'image du QR code à partir de l'identifiant stocké, pour
  // affichage et impression — jusqu'ici seul le texte brut était affiché,
  // sans aucun moyen de le scanner ou de l'imprimer depuis cette page.
  useEffect(() => {
    if (tag?.type_tag === 'qr' && tag?.qr_code) {
      QRCode.toDataURL(tag.qr_code, { width: 220, margin: 1, color: { dark: '#000000', light: '#FFFFFF' } })
        .then(setQrImageUrl)
        .catch(() => setQrImageUrl(null));
    } else {
      setQrImageUrl(null);
    }
  }, [tag]);

  const handleImprimer = () => {
    window.print();
  };

  const fetchTagDetails = async () => {
    setLoading(true);
    setServerError("");
    setInfoMsg("");
    
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/tags/get_tags.php?id=${id}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (response.data?.success) {
        setTag(response.data.data);
      } else {
        setServerError(response.data?.message || "Tag non trouvé.");
      }
    } catch (err) {
      handleFetchError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchError = (err) => {
    if (err.response?.status === 401) {
      setServerError("Session expirée. Veuillez vous reconnecter.");
      localStorage.removeItem("access_token");
      setTimeout(() => navigate("/login"), 2000);
    } else if (err.response?.status === 403) {
      setServerError("Accès interdit. Vous n'avez pas les permissions nécessaires.");
    } else if (err.response?.status === 404) {
      setServerError("Tag non trouvé. Il a peut-être été supprimé.");
    } else if (err.response?.status === 400) {
      setServerError("ID tag invalide.");
    } else if (err.response?.status === 500) {
      setServerError("Erreur serveur. Veuillez contacter l'administrateur.");
    } else if (err.request) {
      setServerError("Serveur inaccessible. Vérifiez votre connexion.");
    } else {
      setServerError("Erreur technique lors du chargement des détails du tag.");
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString || dateString === "0000-00-00 00:00:00") return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const handleBackToList = () => {
    navigate("/tags");
  };

  const handleEditTag = () => {
    navigate(`/tags/modifier/${tag.id_tag}`);
  };

  // Fonction pour obtenir les informations du type de tag
  const getTagTypeInfo = (typeTag) => {
    switch(typeTag) {
      case 'qr':
        return {
          label: 'QR Code',
          icon: 'bi-qr-code',
          color: '#4CAF50',
          bgColor: '#E8F5E9',
          description: 'Tag avec code QR scannable'
        };
      case 'nfc':
        return {
          label: 'NFC',
          icon: 'bi-nfc',
          color: '#2196F3',
          bgColor: '#E3F2FD',
          description: 'Tag NFC sans contact'
        };
      default:
        return {
          label: 'Inconnu',
          icon: 'bi-tag',
          color: '#9E9E9E',
          bgColor: '#F5F5F5',
          description: 'Type de tag non spécifié'
        };
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Détails du Tag</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/tags">Tags</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Chargement...</li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body">
              <div className="text-center py-5">
                <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des détails du tag...</p>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (serverError) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Détails du Tag</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/tags" style={{ color: COLORS.primary }}>Tags</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Erreur</li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body">
              <div className="alert alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">
                    {serverError.split('\n').map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setServerError("")}
                    data-bs-toggle="tooltip"
                    data-bs-placement="bottom"
                    data-bs-title="Fermer l'alerte"
                  ></button>
                </div>
              </div>
              <button
                className="btn fw-semibold"
                onClick={handleBackToList}
                style={{
                  color: COLORS.primary,
                  borderColor: COLORS.primary,
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  padding: '10px 24px'
                }}
                data-bs-toggle="tooltip"
                data-bs-placement="bottom"
                data-bs-title="Retour à la liste des tags"
              >
                <i className="bi bi-arrow-left me-2"></i>
                Retour à la liste
              </button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (!tag) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Détails du Tag</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/tags" style={{ color: COLORS.primary }}>Tags</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Non trouvé</li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body">
              <div className="alert alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#fff3e0',
                borderColor: COLORS.warning,
                color: '#f57c00',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.warning}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">
                    Aucune donnée disponible pour ce tag.
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => navigate("/tags")}
                    data-bs-toggle="tooltip"
                    data-bs-placement="bottom"
                    data-bs-title="Fermer et retourner à la liste"
                  ></button>
                </div>
              </div>
              <button
                className="btn fw-semibold"
                onClick={handleBackToList}
                style={{
                  color: COLORS.primary,
                  borderColor: COLORS.primary,
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  padding: '10px 24px'
                }}
                data-bs-toggle="tooltip"
                data-bs-placement="bottom"
                data-bs-title="Retour à la liste des tags"
              >
                <i className="bi bi-arrow-left me-2"></i>
                Retour à la liste
              </button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  const isActive = tag.statut_tag === "actif";
  const sitesAffectes = tag.sites_affectes || [];
  const hasSites = sitesAffectes.length > 0;
  const tagTypeInfo = getTagTypeInfo(tag.type_tag);

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion des Tags</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/tags" style={{ color: COLORS.primary }}>Tags</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Détails du tag</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                {/* En-tête avec titre et boutons */}
                <div className="d-flex justify-content-between align-items-center mb-5 pb-4 border-bottom" style={{ borderColor: COLORS.lightBg }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-flex align-items-center justify-content-center me-4" style={{ 
                      width: '60px',
                      height: '60px',
                      backgroundColor: tagTypeInfo.color,
                      color: 'white',
                      boxShadow: `0 4px 8px rgba(${parseInt(tagTypeInfo.color.slice(1,3), 16)}, ${parseInt(tagTypeInfo.color.slice(3,5), 16)}, ${parseInt(tagTypeInfo.color.slice(5,7), 16)}, 0.2)`
                    }}
                    data-bs-toggle="tooltip"
                    data-bs-placement="bottom"
                    data-bs-title={tagTypeInfo.description}
                    >
                      <i className={`bi ${tagTypeInfo.icon} fs-4`}></i>
                    </div>
                    <div>
                      <h3 className="card-title mb-1" style={{ color: COLORS.primary }}>
                        {tagTypeInfo.label} - {tag.info_tag || "Tag sans nom"}
                      </h3>
                      <p className="text-muted mb-0" style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                        <i className="bi bi-info-circle me-1"></i>
                        {tagTypeInfo.description}
                      </p>
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      className="btn fw-semibold"
                      onClick={handleBackToList}
                      style={{
                        color: COLORS.primary,
                        borderColor: COLORS.primary,
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '10px 20px'
                      }}
                      data-bs-toggle="tooltip"
                      data-bs-placement="bottom"
                      data-bs-title="Retour à la liste des tags"
                    >
                      <i className="bi bi-arrow-left me-2"></i>
                      Retour
                    </button>
                    <button
                      className="btn fw-semibold"
                      onClick={handleEditTag}
                      style={{
                        color: COLORS.white,
                        backgroundColor: COLORS.primary,
                        borderColor: COLORS.primary,
                        borderRadius: '8px',
                        padding: '10px 24px'
                      }}
                      data-bs-toggle="tooltip"
                      data-bs-placement="bottom"
                      data-bs-title="Modifier les informations de ce tag"
                    >
                      <i className="bi bi-pencil me-2"></i>
                      Modifier
                    </button>
                  </div>
                </div>

                {infoMsg && (
                  <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#e8f5e9',
                    borderColor: COLORS.success,
                    color: '#2e7d32',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.success}`
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-check-circle me-2"></i>
                      <div className="flex-grow-1">
                        {infoMsg.split('\n').map((line, i) => (
                          <div key={i}>{line}</div>
                        ))}
                      </div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setInfoMsg("")}
                        data-bs-toggle="tooltip"
                        data-bs-placement="bottom"
                        data-bs-title="Fermer le message"
                      ></button>
                    </div>
                  </div>
                )}

                {/* Informations principales */}
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card mb-4" style={{ 
                      borderRadius: '8px',
                      border: `1px solid ${COLORS.lightBg}`,
                      boxShadow: 'none'
                    }}>
                      <div className="card-body">
                        <h6 className="card-title mb-4" style={{ 
                          color: COLORS.primary,
                          fontWeight: '600'
                        }}>
                          <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                            width: '30px',
                            height: '30px',
                            backgroundColor: '#E3F2FD',
                            color: COLORS.primary
                          }}>
                            <i className="bi bi-info-circle"></i>
                          </div>
                          Informations du tag
                        </h6>
                        
                        <div className="row">
                          {/* Colonne gauche : Statut, Type et Dates */}
                          <div className="col-md-6 mb-4">
                            <div className="p-4 rounded" style={{ 
                              backgroundColor: '#F8F9FA',
                              borderLeft: `4px solid ${COLORS.primary}`
                            }}>
                              <div className="mb-4">
                                <small className="text-muted d-block mb-2" style={{ fontSize: '0.85rem' }}>
                                  <strong>Statut</strong>
                                </small>
                                <span className="badge fw-semibold" style={{ 
                                  backgroundColor: isActive ? '#e8f5e9' : '#ffebee',
                                  color: isActive ? '#2e7d32' : '#c62828',
                                  padding: '8px 16px',
                                  borderRadius: '6px',
                                  fontSize: '0.9rem'
                                }}
                                data-bs-toggle="tooltip"
                                data-bs-placement="bottom"
                                data-bs-title={isActive ? "Tag actif - Peut être affecté à un site" : "Tag inactif - Doit être activé pour être utilisé"}
                                >
                                  {isActive ? (
                                    <>
                                      <i className="bi bi-check-circle me-1"></i>
                                      Actif
                                    </>
                                  ) : (
                                    <>
                                      <i className="bi bi-x-circle me-1"></i>
                                      Inactif
                                    </>
                                  )}
                                </span>
                              </div>
                              
                              <div className="mb-4">
                                <small className="text-muted d-block mb-2" style={{ fontSize: '0.85rem' }}>
                                  <strong>Type de tag</strong>
                                </small>
                                <div className="d-flex align-items-center">
                                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                                    width: '36px',
                                    height: '36px',
                                    backgroundColor: tagTypeInfo.bgColor,
                                    color: tagTypeInfo.color,
                                    fontSize: '0.9rem'
                                  }}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="bottom"
                                  data-bs-title={tagTypeInfo.description}
                                  >
                                    <i className={`bi ${tagTypeInfo.icon}`}></i>
                                  </div>
                                  <div>
                                    <div style={{ color: COLORS.darkText, fontWeight: '500' }}>
                                      {tagTypeInfo.label}
                                    </div>
                                    <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                                      {tagTypeInfo.description}
                                    </small>
                                  </div>
                                </div>
                              </div>
                              
                              {tag.qr_code && (
                                <div className="mb-4">
                                  <small className="text-muted d-block mb-2" style={{ fontSize: '0.85rem' }}>
                                    <strong>Code QR</strong>
                                  </small>
                                  <div>
                                    <div style={{ 
                                      color: COLORS.darkText,
                                      fontWeight: '500',
                                      padding: '8px 12px',
                                      backgroundColor: 'white',
                                      borderRadius: '6px',
                                      border: '1px solid #e0e0e0'
                                    }}
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="bottom"
                                    data-bs-title="Identifiant unique du QR Code"
                                    >
                                      {tag.qr_code}
                                    </div>
                                    <small className="text-muted mt-1 d-block" style={{ fontSize: '0.8rem' }}>
                                      Identifiant unique du QR Code
                                    </small>

                                    {qrImageUrl && (
                                      <div className="mt-3 p-3 text-center bg-white rounded" style={{ border: '1px solid #e0e0e0' }} id="print-area">
                                        <img src={qrImageUrl} alt="QR code du tag" style={{ width: '160px', height: '160px' }} />
                                        <div className="mt-2" style={{ color: COLORS.darkText, fontWeight: '600', fontSize: '0.9rem' }}>
                                          {tag.info_tag || 'Tag sans nom'}
                                        </div>
                                        <button
                                          type="button"
                                          className="btn btn-sm fw-semibold mt-2 no-print"
                                          onClick={handleImprimer}
                                          style={{ color: COLORS.primary, borderColor: COLORS.primary, borderRadius: '6px' }}
                                        >
                                          <i className="bi bi-printer me-1"></i>
                                          Imprimer
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              {tag.nfc_uid && (
                                <div className="mb-4">
                                  <small className="text-muted d-block mb-2" style={{ fontSize: '0.85rem' }}>
                                    <strong>UID NFC</strong>
                                  </small>
                                  <div>
                                    <div style={{ 
                                      color: COLORS.darkText,
                                      fontWeight: '500',
                                      padding: '8px 12px',
                                      backgroundColor: 'white',
                                      borderRadius: '6px',
                                      border: '1px solid #e0e0e0'
                                    }}
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="bottom"
                                    data-bs-title="Identifiant unique NFC"
                                    >
                                      {tag.nfc_uid}
                                    </div>
                                    <small className="text-muted mt-1 d-block" style={{ fontSize: '0.8rem' }}>
                                      Identifiant unique NFC
                                    </small>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Colonne droite : Informations et Dates */}
                          <div className="col-md-6 mb-4">
                            <div className="p-4 rounded" style={{ 
                              backgroundColor: '#F8F9FA',
                              borderLeft: `4px solid ${COLORS.secondary}`
                            }}>
                              <div className="mb-4">
                                <small className="text-muted d-block mb-2" style={{ fontSize: '0.85rem' }}>
                                  <strong>Description</strong>
                                </small>
                                <div className="p-3 bg-white rounded" style={{ 
                                  border: `1px solid ${COLORS.lightBg}`,
                                  minHeight: '100px'
                                }}>
                                  <p className="mb-0" style={{ color: COLORS.darkText }}>
                                    {tag.info_tag || "Aucune description disponible"}
                                  </p>
                                </div>
                              </div>
                              
                              <div className="mb-4">
                                <small className="text-muted d-block mb-2" style={{ fontSize: '0.85rem' }}>
                                  <strong>Date de création</strong>
                                </small>
                                <div className="d-flex align-items-center">
                                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                                    width: '36px',
                                    height: '36px',
                                    backgroundColor: '#E3F2FD',
                                    color: COLORS.primary,
                                    fontSize: '0.9rem'
                                  }}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="bottom"
                                  data-bs-title="Date et heure de création du tag"
                                  >
                                    <i className="bi bi-calendar-plus"></i>
                                  </div>
                                  <div>
                                    <div style={{ color: COLORS.darkText, fontWeight: '500' }}>
                                      {formatDateTime(tag.created_at).split(' ')[0]}
                                    </div>
                                    <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                                      {formatDateTime(tag.created_at).split(' ')[1]}
                                    </small>
                                  </div>
                                </div>
                              </div>
                              
                              <div>
                                <small className="text-muted d-block mb-2" style={{ fontSize: '0.85rem' }}>
                                  <strong>Dernière modification</strong>
                                </small>
                                <div className="d-flex align-items-center">
                                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                                    width: '36px',
                                    height: '36px',
                                    backgroundColor: '#E3F2FD',
                                    color: COLORS.primary,
                                    fontSize: '0.9rem'
                                  }}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="bottom"
                                  data-bs-title="Date et heure de la dernière modification"
                                  >
                                    <i className="bi bi-calendar-check"></i>
                                  </div>
                                  <div>
                                    <div style={{ color: COLORS.darkText, fontWeight: '500' }}>
                                      {tag.updated_at && tag.updated_at !== "0000-00-00 00:00:00"
                                        ? formatDateTime(tag.updated_at).split(' ')[0]
                                        : "-"}
                                    </div>
                                    <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                                      {tag.updated_at && tag.updated_at !== "0000-00-00 00:00:00"
                                        ? formatDateTime(tag.updated_at).split(' ')[1]
                                        : "Aucune modification"}
                                    </small>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sites affectés */}
                <div className="card mb-0" style={{ 
                  borderRadius: '8px',
                  border: `1px solid ${COLORS.lightBg}`,
                  boxShadow: 'none'
                }}>
                  <div className="card-body">
                    <h6 className="card-title mb-4" style={{ 
                      color: COLORS.primary,
                      fontWeight: '600'
                    }}>
                      <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                        width: '30px',
                        height: '30px',
                        backgroundColor: '#E3F2FD',
                        color: COLORS.primary
                      }}>
                        <i className="bi bi-building"></i>
                      </div>
                      Sites affectés
                      <span className="badge fw-semibold ms-3" style={{ 
                        backgroundColor: COLORS.lightBg,
                        color: COLORS.primary,
                        padding: '4px 12px',
                        borderRadius: '6px',
                        fontSize: '0.8rem'
                      }}
                      data-bs-toggle="tooltip"
                      data-bs-placement="bottom"
                      data-bs-title={`${sitesAffectes.length} site(s) affecté(s) à ce tag`}
                      >
                        {sitesAffectes.length} site(s)
                      </span>
                    </h6>
                    
                    {!hasSites ? (
                      <div className="text-center py-5">
                        <div className="mb-4">
                          <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                            width: '80px',
                            height: '80px',
                            backgroundColor: '#F5F7FA',
                            color: '#B0BEC5'
                          }}
                          data-bs-toggle="tooltip"
                          data-bs-placement="bottom"
                          data-bs-title="Aucun site affecté"
                          >
                            <i className="bi bi-building fs-1"></i>
                          </div>
                        </div>
                        <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucun site affecté</h6>
                        <p className="text-muted mb-0" style={{ color: COLORS.darkText, maxWidth: '400px', margin: '0 auto' }}>
                          Ce tag n'est pas encore affecté à un site.
                        </p>
                      </div>
                    ) : (
                      <div className="row">
                        {sitesAffectes.map((site, index) => (
                          <div key={index} className="col-md-6 mb-3">
                            <div className="card border" style={{ 
                              borderRadius: '8px',
                              borderColor: '#e0e0e0',
                              transition: 'all 0.3s ease',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                            }}
                            data-bs-toggle="tooltip"
                            data-bs-placement="bottom"
                            data-bs-title={`Site: ${site.nom_site} - Statut: ${site.statut_site === 'actif' ? 'Actif' : 'Inactif'}`}
                            >
                              <div className="card-body p-3">
                                <div className="d-flex justify-content-between align-items-start">
                                  <div className="flex-grow-1">
                                    <div className="d-flex align-items-center mb-3">
                                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                                        width: '36px',
                                        height: '36px',
                                        backgroundColor: COLORS.secondary,
                                        color: 'white',
                                        boxShadow: '0 2px 4px rgba(30, 136, 200, 0.2)'
                                      }}>
                                        <i className="bi bi-building"></i>
                                      </div>
                                      <div>
                                        <h6 className="mb-0" style={{ color: COLORS.darkText }}>
                                          {site.nom_site}
                                        </h6>
                                      </div>
                                    </div>
                                    
                                    <div className="ms-4">
                                      {site.addresse_site && (
                                        <div className="d-flex align-items-center mb-2">
                                          <i className="bi bi-geo-alt me-2" style={{ color: COLORS.secondary, fontSize: '0.9rem' }}></i>
                                          <span className="text-muted small">{site.addresse_site}</span>
                                        </div>
                                      )}
                                      
                                      <div className="d-flex gap-2 flex-wrap mt-3">
                                        <span className="badge fw-semibold" style={{ 
                                          backgroundColor: site.statut_site === 'actif' ? '#e8f5e9' : '#ffebee',
                                          color: site.statut_site === 'actif' ? '#2e7d32' : '#c62828',
                                          padding: '6px 12px',
                                          borderRadius: '6px',
                                          fontSize: '0.75rem'
                                        }}
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="bottom"
                                        data-bs-title={`Statut du site: ${site.statut_site === 'actif' ? 'Actif' : 'Inactif'}`}
                                        >
                                          {site.statut_site === 'actif' ? 'Actif' : 'Inactif'}
                                        </span>
                                        
                                        {(site.latitude_site && site.longitude_site) ? (
                                          <span className="badge fw-semibold" style={{ 
                                            backgroundColor: '#E3F2FD',
                                            color: COLORS.primary,
                                            padding: '6px 12px',
                                            borderRadius: '6px',
                                            fontSize: '0.75rem'
                                          }}
                                          data-bs-toggle="tooltip"
                                          data-bs-placement="bottom"
                                          data-bs-title="Ce site a une localisation GPS définie"
                                          >
                                            <i className="bi bi-geo-alt me-1"></i>
                                            Localisation GPS
                                          </span>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .alert strong {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .form-control,
        .alert,
        small {
          font-family: 'Roboto', sans-serif;
        }
        
        .btn:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(11, 60, 93, 0.25);
        }
        
        .btn,
        .badge,
        .card {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        /* Style des tooltips Bootstrap */
        .tooltip {
          font-family: 'Roboto', sans-serif;
          font-size: 0.85rem;
        }
        
        .tooltip-inner {
          background-color: ${COLORS.primary};
          color: white;
          border-radius: 6px;
          padding: 6px 12px;
          max-width: 300px;
        }
        
        .tooltip-arrow {
          display: none;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .col-md-6 {
            margin-bottom: 16px;
          }
        }
        
        /* Amélioration de la lisibilité */
        .badge:hover {
          transform: translateY(-1px);
        }

        /* Impression : n'imprimer que le QR code + le nom du tag */
        @media print {
          body * {
            visibility: hidden;
          }
          #print-area, #print-area * {
            visibility: visible;
          }
          #print-area {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            border: none !important;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>
    </Layout>
  );
};

export default TagDetails;